/**
 * Everything the Events and Stats pages do that is not rendering.
 *
 * The filter RULES are not here. Which rows a filter keeps (the "Not at TD
 * Garden" pseudo-venue, a special-event category matching all its types,
 * "Untagged" meaning no attendees and not solo) lives in
 * `sports_tracker/filters_logic.py`, which the API calls. The browser sends
 * the selection and renders what comes back, including the option lists,
 * so client and server cannot disagree about what a filter means.
 *
 * What is here: the selection's round trip through the URL, which is what
 * lets Events and Stats share one filter and makes a filtered view a link.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";

export interface Special {
  slug: string;
  /** "2026 Winter Classic", already carrying the year. */
  label: string;
  emoji: string;
  icon_url: string | null;
}

export type GameStatus = "scheduled" | "in_progress" | "final";

export interface EventRow {
  id: number;
  /** Venue-local calendar day, YYYY-MM-DD. */
  event_date: string;
  league: string;
  sport: string;
  sport_emoji: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  venue_name: string | null;
  venue_city: string | null;
  status: GameStatus;
  game_type: string;
  special: Special | null;
  solo: boolean;
  attendee_ids: number[];
}

export interface Person {
  id: number;
  name: string;
  /** Google photo, else Gravatar, else an initials image. Never empty. */
  avatar_url: string;
}

export interface Option {
  value: string;
  label: string;
}

export interface FilterOptions {
  sport: Option[];
  league: Option[];
  team: Option[];
  venue: Option[];
  game_type: Option[];
  /** Just "All" when no event is a special event; the panel then hides it. */
  special: Option[];
  attendee: Option[];
  year: Option[];
}

/** The whole payload of GET /api/events. */
export interface EventsDto {
  /** Every event before filtering, for "12 of 367". */
  total: number;
  events: EventRow[];
  people: Person[];
  logos: Record<string, string>;
  venue_images: Record<string, string>;
  league_badges: Record<string, string>;
  sport_emoji: Record<string, string>;
  options: FilterOptions;
  /** Games still being played or not yet played that ESPN can update. */
  non_final: number;
}

export const FILTER_KEYS = [
  "sport", "league", "team", "side", "venue", "game_type", "special", "with", "year",
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type Filters = Record<FilterKey, string>;

export const NO_FILTERS: Filters = Object.fromEntries(
  FILTER_KEYS.map((k) => [k, "All"]),
) as Filters;

/** The selection from the URL. Anything absent or unknown is "All". */
export function filtersFromSearch(sp: URLSearchParams): Filters {
  const f = { ...NO_FILTERS };
  for (const k of FILTER_KEYS) {
    const v = sp.get(k);
    if (v) f[k] = v;
  }
  return f;
}

/** The selection as query params, omitting every "All" so a clean view is a clean URL. */
export function filtersToSearch(f: Filters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const k of FILTER_KEYS) if (f[k] && f[k] !== "All") sp.set(k, f[k]);
  return sp;
}

/**
 * A new selection with `key` set, clearing what that change invalidates.
 *
 * The hierarchy is Sport -> League -> Team -> Side, exactly the sidebar's:
 * changing a level leaves the levels below it pointing at options that may no
 * longer exist, so they reset to "All".
 */
export function withFilter(f: Filters, key: FilterKey, value: string): Filters {
  const next = { ...f, [key]: value };
  if (key === "sport") Object.assign(next, { league: "All", team: "All", side: "All" });
  if (key === "league") Object.assign(next, { team: "All", side: "All" });
  if (key === "team") next.side = "All";
  return next;
}

/** The filters currently narrowing the view; Side only rides along with a team. */
export function activeFilters(f: Filters): FilterKey[] {
  return FILTER_KEYS.filter((k) => k !== "side" && f[k] !== "All");
}

/** The label an option list gives a value, falling back to the value itself. */
export function labelFor(options: Option[] | undefined, value: string): string {
  return options?.find((o) => o.value === value)?.label ?? value;
}

export async function fetchEvents(f: Filters): Promise<EventsDto> {
  const qs = filtersToSearch(f).toString();
  const res = await authedFetch(`/api/events${qs ? `?${qs}` : ""}`);
  return (await orThrow(res, "Could not load events")) as EventsDto;
}

export interface YearGroup {
  year: string;
  events: EventRow[];
}

/** Rows grouped under their year, in the order the server sent them (newest first). */
export function groupByYear(events: EventRow[]): YearGroup[] {
  const out: YearGroup[] = [];
  for (const e of events) {
    const y = e.event_date.slice(0, 4);
    const last = out[out.length - 1];
    if (last && last.year === y) last.events.push(e);
    else out.push({ year: y, events: [e] });
  }
  return out;
}

/**
 * Who went to a game. `newNames` are people typed in who do not exist yet;
 * the server creates them.
 */
export async function setAttendees(
  eventId: number,
  personIds: number[],
  newNames: string[] = [],
): Promise<void> {
  const res = await authedFetch(`/api/events/${eventId}/attendees`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ person_ids: personIds, new_names: newNames }),
  });
  await orThrow(res, "Could not save who went");
}

export async function setSolo(eventId: number, solo: boolean): Promise<void> {
  const res = await authedFetch(`/api/events/${eventId}/solo`, {
    method: solo ? "POST" : "DELETE",
  });
  await orThrow(res, solo ? "Could not mark it as went alone" : "Could not undo went alone");
}

/** Re-ask ESPN about every game not yet final. */
export async function refreshScores(): Promise<{ checked: number; updated: number }> {
  const res = await authedFetch("/api/events/refresh", { method: "POST" });
  return (await orThrow(res, "Could not refresh scores")) as { checked: number; updated: number };
}
